import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

// =============================================================================
// Bandeja animada para os <select> nativos.
//
// O popup do <select> nativo é desenhado pelo navegador/SO e não aceita
// animação. Este componente (montado UMA vez em index.js) intercepta o clique
// do mouse — e o toque, no celular — num <select> comum, abre uma bandeja
// própria logo abaixo dele (animada em styles/animations.css) e aplica a
// escolha no próprio <select>, disparando o `change`: todo `onChange`/`register`
// do React segue funcionando sem mudar nenhuma tela.
//
// Abrir um select enquanto outro está aberto fecha o anterior (com a animação de
// saída, ao mesmo tempo em que o novo abre).
//
// Fica fora (comportamento nativo): <select multiple> ou com `size`, selects
// desabilitados e os marcados com `data-native-select`. O teclado com o select
// focado continua nativo; com a bandeja aberta, as setas/Enter/Esc a controlam.
// =============================================================================

const EXIT_MS = 130;
const MAX_HEIGHT = 280;
const GAP = 4;
const TOUCH_MOVE_TOLERANCE = 10; // px: acima disso o toque é rolagem, não toque
const TOUCH_GHOST_MS = 800; // mousedown sintético que pode vir depois do toque

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isEligibleSelect = (select) =>
  Boolean(select) &&
  !select.disabled &&
  !select.multiple &&
  !(select.size > 1) &&
  !select.hasAttribute("data-native-select");

const readOptions = (select) => {
  const items = [];
  const push = (option, group) => {
    items.push({
      type: "option",
      index: option.index,
      label: option.label || option.textContent || "",
      disabled: option.disabled || Boolean(group?.disabled),
      selected: option.selected,
    });
  };
  Array.from(select.children).forEach((child) => {
    if (child.tagName === "OPTGROUP") {
      items.push({ type: "group", label: child.label });
      Array.from(child.children).forEach((option) => push(option, child));
    } else if (child.tagName === "OPTION") {
      push(child);
    }
  });
  return items;
};

let trayCounter = 0;

// Algumas telas recriam o <select> a cada render (componente declarado dentro
// de outro componente, ex.: o seletor de título do editor). Se o elemento que
// abriu a bandeja saiu do DOM, procura o que ficou no lugar dele.
const resolveSelect = (tray) => {
  const { select, rect } = tray;
  if (select.isConnected) return select;
  const replacement = document.elementFromPoint(
    rect.left + rect.width / 2,
    rect.top + rect.height / 2,
  );
  const candidate = replacement ? replacement.closest("select") : null;
  return candidate && candidate.isConnected ? candidate : null;
};

function TrayView({ tray, active, closing, onChoose, onHover, trayRef, listRef }) {
  const { rect, items, up, select } = tray;
  const width = Math.max(rect.width, 140);
  // Altura: no celular (teclado/barra de endereço) sobra pouco espaço acima ou
  // abaixo do select; limita à folga real para a bandeja não sair da tela e a
  // lista rolar por dentro.
  const room = up ? rect.top - GAP - 8 : window.innerHeight - rect.bottom - GAP - 8;
  const maxHeight = Math.min(MAX_HEIGHT, Math.max(120, room));
  const style = {
    position: "fixed",
    left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    minWidth: width,
    maxWidth: "calc(100vw - 16px)",
    maxHeight,
    zIndex: 10050,
    fontFamily: window.getComputedStyle(select).fontFamily,
    ...(up
      ? { bottom: window.innerHeight - rect.top + GAP }
      : { top: rect.bottom + GAP }),
  };

  return (
    <div
      ref={trayRef}
      role="listbox"
      className={`rs-select-tray${up ? " rs-select-tray--up" : ""}${closing ? " rs-select-tray--closing" : ""}`}
      style={style}
    >
      <div ref={listRef} className="rs-select-tray__list">
        {items.map((item, i) =>
          item.type === "group" ? (
            <div key={`g${i}`} className="rs-select-tray__group">
              {item.label}
            </div>
          ) : (
            <div
              key={`o${item.index}`}
              role="option"
              aria-selected={item.selected}
              aria-disabled={item.disabled || undefined}
              data-active={!closing && active === item.index}
              className={`rs-select-tray__option${item.selected ? " is-selected" : ""}${item.disabled ? " is-disabled" : ""}`}
              onMouseEnter={() => !closing && !item.disabled && onHover(item.index)}
              onClick={() => !closing && onChoose(item)}
            >
              {item.label || " "}
            </div>
          ),
        )}
      </div>
    </div>
  );
}

export default function SelectTray() {
  const [current, setCurrent] = useState(null); // bandeja aberta
  const [leaving, setLeaving] = useState([]); // bandejas em animação de saída
  const [active, setActive] = useState(-1);
  const trayRef = useRef(null);
  const listRef = useRef(null);
  const currentRef = useRef(null);
  const timersRef = useRef(new Set());
  const lastTouchOpenRef = useRef(0);
  currentRef.current = current;

  // Move a bandeja aberta para a lista de saída (animada) e a remove depois.
  const retire = useCallback((tray, immediate) => {
    if (!tray) return;
    if (immediate || prefersReducedMotion()) return;
    // Sem duplicar: o `blur` do select anterior também pode pedir o fechamento.
    setLeaving((list) =>
      list.some((t) => t.id === tray.id) ? list : [...list, tray],
    );
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      setLeaving((list) => list.filter((t) => t.id !== tray.id));
    }, EXIT_MS);
    timersRef.current.add(timer);
  }, []);

  const close = useCallback(
    (immediate = false) => {
      const tray = currentRef.current;
      if (!tray) return;
      currentRef.current = null;
      setCurrent(null);
      retire(tray, immediate);
    },
    [retire],
  );

  const openFor = useCallback(
    (select) => {
      const previous = currentRef.current;
      if (previous && previous.select === select) {
        close();
        return;
      }
      const rect = select.getBoundingClientRect();
      const items = readOptions(select);
      const below = window.innerHeight - rect.bottom - GAP - 8;
      const above = rect.top - GAP - 8;
      const wanted = Math.min(MAX_HEIGHT, items.length * 36 + 8);
      const up = below < wanted && above > below;
      const tray = { id: ++trayCounter, select, items, rect, up };

      // O anterior sai (animado) no mesmo instante em que o novo entra. O novo já
      // é a bandeja atual ANTES do foco mudar: o `blur` do select anterior só
      // fecha a bandeja se ela ainda for a dele.
      if (previous) retire(previous, false);
      currentRef.current = tray;
      setCurrent(tray);
      setActive(items.findIndex((i) => i.type === "option" && i.selected));
      select.focus({ preventScroll: true });
    },
    [close, retire],
  );

  const choose = useCallback(
    (item) => {
      const tray = currentRef.current;
      if (!tray || item.disabled) return;
      const select = resolveSelect(tray);
      if (!select) {
        close();
        return;
      }
      if (select.selectedIndex !== item.index) {
        select.selectedIndex = item.index;
        select.dispatchEvent(new Event("input", { bubbles: true }));
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      close();
      select.focus({ preventScroll: true });
    },
    [close],
  );

  // Mouse: abre no clique; clique fora fecha.
  useEffect(() => {
    const onMouseDown = (event) => {
      if (event.button !== 0) return;
      const target = event.target;

      // Clique dentro da bandeja: não deixa modais ("clique fora") fecharem e
      // não tira o foco do select.
      if (trayRef.current && trayRef.current.contains(target)) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }

      const select = target instanceof Element ? target.closest("select") : null;

      // Mousedown "fantasma" que alguns navegadores geram depois do toque que
      // já abriu a bandeja: só impede o popup nativo.
      if (
        select &&
        isEligibleSelect(select) &&
        Date.now() - lastTouchOpenRef.current < TOUCH_GHOST_MS
      ) {
        event.preventDefault();
        return;
      }

      if (!isEligibleSelect(select)) {
        close();
        return;
      }
      event.preventDefault(); // impede o popup nativo (o resto do evento segue)
      openFor(select);
    };
    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
  }, [close, openFor]);

  // Toque (celular/tablet): um toque parado no select abre a bandeja; arrastar
  // continua rolando a página. O toque fora da bandeja a fecha.
  useEffect(() => {
    let pending = null; // { select, x, y }

    const onTouchStart = (event) => {
      const target = event.target;
      pending = null;
      if (trayRef.current && trayRef.current.contains(target)) return;
      const select = target instanceof Element ? target.closest("select") : null;
      if (event.touches.length === 1 && isEligibleSelect(select)) {
        pending = {
          select,
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
        return;
      }
      close();
    };
    const onTouchMove = (event) => {
      if (!pending || !event.touches.length) return;
      const dx = event.touches[0].clientX - pending.x;
      const dy = event.touches[0].clientY - pending.y;
      if (Math.hypot(dx, dy) > TOUCH_MOVE_TOLERANCE) pending = null;
    };
    const onTouchEnd = (event) => {
      if (!pending) return;
      const { select } = pending;
      pending = null;
      if (!event.cancelable) return; // não dá para segurar o popup nativo
      event.preventDefault(); // sem clique/foco sintético: o nativo não abre
      lastTouchOpenRef.current = Date.now();
      openFor(select);
    };
    const onTouchCancel = () => {
      pending = null;
    };

    document.addEventListener("touchstart", onTouchStart, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchmove", onTouchMove, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchend", onTouchEnd, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchcancel", onTouchCancel, true);
    return () => {
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("touchend", onTouchEnd, true);
      document.removeEventListener("touchcancel", onTouchCancel, true);
    };
  }, [close, openFor]);

  // Rolagem/redimensionamento: a bandeja acompanha o select (reposiciona); só
  // fecha se ele sair da tela. Rolagens que não o movem (ex.: o foco de um editor
  // rolando a página) são ignoradas. Mudar a LARGURA da janela fecha (a barra de
  // endereço do celular muda só a altura). O `blur` do select também fecha.
  useEffect(() => {
    if (!current) return undefined;
    const startWidth = window.innerWidth;
    const follow = () => {
      const tray = currentRef.current;
      if (!tray) return;
      const select = resolveSelect(tray);
      if (!select) {
        close(true);
        return;
      }
      const rect = select.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        close(true);
        return;
      }
      const moved =
        Math.abs(rect.top - tray.rect.top) > 0.5 ||
        Math.abs(rect.left - tray.rect.left) > 0.5 ||
        Math.abs(rect.width - tray.rect.width) > 0.5;
      if (moved) {
        const next = { ...tray, rect };
        currentRef.current = next;
        setCurrent(next);
      }
    };
    const onScroll = (event) => {
      if (trayRef.current && trayRef.current.contains(event.target)) return;
      follow();
    };
    const onResize = () => {
      if (window.innerWidth !== startWidth) close(true);
      else follow();
    };
    const onBlur = (event) => {
      // Select recriado pela tela (saiu do DOM): o blur é só da remoção.
      if (!current.select.isConnected) return;
      if (
        event.relatedTarget &&
        trayRef.current &&
        trayRef.current.contains(event.relatedTarget)
      ) {
        return;
      }
      if (currentRef.current && currentRef.current.select === current.select) {
        close();
      }
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    current.select.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      current.select.removeEventListener("blur", onBlur);
    };
  }, [current, close]);

  // Teclado com a bandeja aberta (o foco continua no <select>).
  useEffect(() => {
    if (!current) return undefined;
    const onKeyDown = (event) => {
      const options = current.items.filter((i) => i.type === "option");
      const enabled = options.filter((i) => !i.disabled);
      if (!enabled.length) return;
      const move = (dir) => {
        const pos = enabled.findIndex((i) => i.index === active);
        const next =
          enabled[
            pos < 0
              ? dir > 0
                ? 0
                : enabled.length - 1
              : Math.min(enabled.length - 1, Math.max(0, pos + dir))
          ];
        setActive(next.index);
      };
      switch (event.key) {
        case "Escape":
          event.preventDefault();
          event.stopPropagation();
          close();
          break;
        case "ArrowDown":
          event.preventDefault();
          move(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          move(-1);
          break;
        case "Home":
          event.preventDefault();
          setActive(enabled[0].index);
          break;
        case "End":
          event.preventDefault();
          setActive(enabled[enabled.length - 1].index);
          break;
        case "Enter":
        case " ": {
          event.preventDefault();
          const item = options.find((i) => i.index === active);
          if (item) choose(item);
          else close();
          break;
        }
        case "Tab":
          close();
          break;
        default:
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [current, active, close, choose]);

  // Leva o item ativo para a área visível.
  useLayoutEffect(() => {
    if (!current || !listRef.current) return;
    const node = listRef.current.querySelector('[data-active="true"]');
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [current, active]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, []);

  if (!current && !leaving.length) return null;

  return createPortal(
    <>
      {leaving.map((tray) => (
        <TrayView
          key={tray.id}
          tray={tray}
          active={-1}
          closing
          onChoose={() => {}}
          onHover={() => {}}
        />
      ))}
      {current && (
        <TrayView
          key={current.id}
          tray={current}
          active={active}
          closing={false}
          onChoose={choose}
          onHover={setActive}
          trayRef={trayRef}
          listRef={listRef}
        />
      )}
    </>,
    document.body,
  );
}
